import { useEffect, useState, useCallback } from "react";
import Toast from "react-bootstrap/Toast";
import ToastContainer from "react-bootstrap/ToastContainer";
import Button from "react-bootstrap/Button";

import { downloadBulkReservedImportErrorCsv } from "../UnifiedSearchList/bulkReservedImportErrorCsv";

function summarizeResults(results) {
  if (!Array.isArray(results) || results.length === 0) return "";
  const ok = results.filter((r) => r.ok).length;
  const total = results.length;
  const failed = total - ok;
  if (failed === 0) {
    return `Created ${ok} order${ok === 1 ? "" : "s"}.`;
  }
  return `Created ${ok} of ${total} orders (${failed} failed).`;
}

/**
 * Global toast when a background bulk reserved import finishes (modal may be closed).
 */
export default function BulkReservedImportToastHost() {
  const [toast, setToast] = useState(null);

  const onEvent = useCallback((e) => {
    const d = e?.detail || {};
    if (d.timeout) {
      setToast({
        variant: "warning",
        title: "Bulk import",
        summary:
          "The import is taking longer than expected. Refresh the list in a moment or check back later.",
        batchId: null,
        failedResults: null,
      });
      return;
    }
    if (d.failed) {
      setToast({
        variant: "danger",
        title: "Bulk import failed",
        summary: String(d.error || d.job?.error || "Unknown error"),
        batchId: null,
        failedResults: null,
      });
      return;
    }
    const results = d.job?.results || d.results;
    const batchId = d.batchId || d.job?.batchId || "";
    const list = Array.isArray(results) ? results : [];
    const failedRows = list.filter((r) => !r.ok);
    const variant =
      list.length === 0 || failedRows.length === 0 ? "success" : "warning";
    setToast({
      variant,
      title: "Bulk import complete",
      summary: summarizeResults(list) || "Import finished.",
      batchId,
      failedResults: failedRows.length ? failedRows : null,
    });
  }, []);

  useEffect(() => {
    window.addEventListener("bulk-reserved-import-complete", onEvent);
    return () =>
      window.removeEventListener("bulk-reserved-import-complete", onEvent);
  }, [onEvent]);

  if (!toast) return null;

  const headerClass =
    toast.variant === "success"
      ? "bg-success text-white"
      : toast.variant === "warning"
        ? "bg-warning text-dark"
        : toast.variant === "danger"
          ? "bg-danger text-white"
          : "";

  return (
    <ToastContainer
      position="bottom-end"
      className="p-3"
      style={{ zIndex: 10800 }}
    >
      <Toast
        onClose={() => setToast(null)}
        delay={toast.failedResults?.length ? 60000 : 10000}
        autohide
        show
      >
        <Toast.Header closeButton className={headerClass}>
          <strong className="me-auto">{toast.title}</strong>
        </Toast.Header>
        <Toast.Body>
          <div>{toast.summary}</div>
          {toast.failedResults?.length ? (
            <div className="mt-2">
              <Button
                type="button"
                size="sm"
                variant="outline-dark"
                onClick={() =>
                  downloadBulkReservedImportErrorCsv(
                    toast.batchId,
                    toast.failedResults,
                  )
                }
              >
                Download error report (CSV)
              </Button>
            </div>
          ) : null}
        </Toast.Body>
      </Toast>
    </ToastContainer>
  );
}
